import Image from 'next/image';
import styleConfig from '@/styles/style-config';
import catchError from '@/lib/utils/catchError';

const deviceSizeMap = ['2xs', 'xs', 'sm', 'md', 'lg', 'xl', '2xl'].reduce(
  (map, sizeKey) => ({
    ...map,
    [sizeKey]: Number(styleConfig.theme.screens[sizeKey]?.replace('px', '')),
  }),
  {},
);

const defaultSizes = [
  `(min-width: ${deviceSizeMap['2xl']}px) ${deviceSizeMap['2xl']}px`,
  `(min-width: ${deviceSizeMap.xl}px) ${deviceSizeMap.xl}px`,
  `(min-width: ${deviceSizeMap.lg}px) ${deviceSizeMap.lg}px`,
  `${deviceSizeMap.md}px`,
].join(',');

const handleContenfulImage = ({ src = '', width, quality }) => {
  const queryParams = ['fm=webp'];

  if (width) {
    queryParams.push(`w=${width}`);
  }

  if (quality) {
    queryParams.push(`q=${quality}`);
  }

  return queryParams.length ? `${src}?${queryParams.join('&')}` : src;
};

const clamp = (value, { min, max }) => {
  if (Number.isNaN(Number(value))) return min;

  return Math.min(Math.max(Number(value), min), max);
};

const encodeUrlParam = (value) => {
  if (!value) return '';

  try {
    return encodeURIComponent(decodeURIComponent(value));
  } catch (error) {
    catchError(null, error);
    return encodeURIComponent(value);
  }
};

const handleBigCommerceImage = ({
  src = '',
  width,
  height,
  quality,
}) => {
  const normalizedWidth = clamp(width || 1200, {
    min: 240,
    max: 1200,
  });

  const normalizedHeight = height
    ? clamp(height, { min: 240, max: 1200 })
    : normalizedWidth;

  const normalizedQuality = clamp(quality || 85, {
    min: 40,
    max: 100,
  });

  const baseUrl = (
    process.env.NEXT_PUBLIC_API_BASE_URL
    || process.env.NEXT_PUBLIC_BASE_URL
    || ''
  );

  const prefixedBaseUrl = baseUrl.endsWith('/')
    ? baseUrl.slice(0, -1)
    : baseUrl;

  const urlParam = encodeUrlParam(src);

  return (
    `${prefixedBaseUrl}/_next/image?url=${urlParam}`
    + `&w=${normalizedWidth}&h=${normalizedHeight}&q=${normalizedQuality}`
  );
};

const loader = (loadedProps) => {
  const { src } = loadedProps;

  if (src?.includes('ctfassets')) {
    return handleContenfulImage(loadedProps);
  }

  if (src?.includes('bigcommerce')) {
    return handleBigCommerceImage(loadedProps);
  }

  return src;
};

const shouldUseSizes = layout => (
  layout === 'fill'
  || layout === 'responsive'
);

const shouldUseFixedDimensions = layout => layout !== 'fill';

const NextImage = ({
  src,
  alt = '',
  height,
  width,
  className,
  style = {},
  objectFit = 'contain',
  objectPosition,
  layout = 'fill',
  quality = 75,
  priority = false,
  preload = false,
  unoptimized = false,
  placeholder,
  blurDataURL,
  sizes,
}) => {
  const isFill = layout === 'fill';
  const shouldPreload = preload || priority;

  const computedSizes = shouldUseSizes(layout)
    ? (sizes || defaultSizes)
    : undefined;

  const computedHeight = shouldUseFixedDimensions(layout)
    ? height
    : undefined;

  const computedWidth = shouldUseFixedDimensions(layout)
    ? width
    : undefined;

  const imageStyle = {
    ...style,
    ...(objectFit ? { objectFit } : {}),
    ...(objectPosition ? { objectPosition } : {}),
  };

  const imageProps = {
    unoptimized,
    className,
    src,
    style: imageStyle,
    sizes: computedSizes,
    alt,
    quality,
    preload: shouldPreload,
    height: computedHeight,
    width: computedWidth,
    fill: isFill,
    placeholder,
    blurDataURL,
  };

  if (process.env.NEXT_PUBLIC_IS_VERCEL === 'true') {
    return <Image {...imageProps} />;
  }

  return (
    <Image
      {...imageProps}
      loader={loader}
    />
  );
};

export default NextImage;
